/**
 * ControlDetailRichView - affiche le detail riche d un controle :
 *  - Texte detaille pour chaque framework (CIS, ISO, NIS2, CyFun)
 *  - Remediation etape-par-etape avec prerequisites, steps, verify, liens
 *
 * Si le controle n a pas encore de detail (le pilote ne couvre que 5),
 * affiche un message informatif "Detail enrichi a venir".
 */
import { useTranslation } from 'react-i18next';
import {
  Card, CardHeader, Title3, Body1, Caption1, Badge, tokens,
} from '@fluentui/react-components';
import {
  Link20Regular, Wrench20Regular, BookOpen20Regular,
  Checkmark20Filled, NumberCircle020Regular,
} from '@fluentui/react-icons';
import { getControlDetail } from '../i18n/control-details';
import type { ControlDetail, FrameworkDetailEntry, CyFunDetailEntry } from '../domain/types';

interface Props {
  controlId: string;
  /** Langue active (par defaut 'fr-BE'). */
  lang?: 'fr-BE' | 'en' | 'nl-BE' | 'de-BE';
}

export function ControlDetailRichView({ controlId, lang = 'fr-BE' }: Props) {
  const { t } = useTranslation();
  const detail = getControlDetail(controlId, lang);

  if (!detail) {
    // Fallback : controle sans detail riche encore
    return (
      <Card style={{ marginTop: 16, padding: 16 }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {t('controls.detail.rich.notAvailable', {
            defaultValue: 'Détail enrichi (CIS / ISO / CyFun / remédiation pas à pas) à venir pour ce contrôle.',
          })}
        </Caption1>
      </Card>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
      {/* ============================================================ */}
      {/* Bloc Frameworks detailles */}
      {/* ============================================================ */}
      <Card>
        <CardHeader
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <BookOpen20Regular style={{ color: tokens.colorBrandForeground1 }} />
              <Title3>
                {t('controls.detail.rich.frameworksHeader', { defaultValue: 'Textes des référentiels' })}
              </Title3>
            </div>
          }
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
          <FrameworkBlock label="CIS Microsoft 365 v3.1" entry={detail.frameworks['CIS-M365']} accent="#875014" />
          <FrameworkBlock label="CIS Azure v2.1"          entry={detail.frameworks['CIS-Azure']} accent="#1B5E91" />
          <FrameworkBlock label="ISO/IEC 27001:2022"      entry={detail.frameworks['ISO27001']}  accent="#114042" />
          <FrameworkBlock label="NIS2 Directive (UE 2022/2555)" entry={detail.frameworks['NIS2']} accent="#0A111E" />
          <CyFunBlock                                     entries={detail.frameworks['CyFun']}   accent="#3B255C" />
        </div>
      </Card>

      {/* ============================================================ */}
      {/* Bloc Remediation pas-a-pas */}
      {/* ============================================================ */}
      <Card>
        <CardHeader
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Wrench20Regular style={{ color: tokens.colorBrandForeground1 }} />
              <Title3>
                {t('controls.detail.rich.remediationHeader', { defaultValue: 'Remédiation pas à pas' })}
              </Title3>
            </div>
          }
        />
        <RemediationBlock remediation={detail.remediation} />
      </Card>
    </div>
  );
}

/* ============================================================ */
/*                  Sous-composants                              */
/* ============================================================ */

function FrameworkBlock({
  label,
  entry,
  accent,
}: {
  label:  string;
  entry?: FrameworkDetailEntry;
  accent: string;
}) {
  if (!entry) return null;
  return (
    <div
      style={{
        padding:    14,
        background: tokens.colorNeutralBackground2,
        borderLeft: `4px solid ${accent}`,
        borderRadius: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Badge appearance="filled" style={{ background: accent, color: '#fff' }}>
          {label}
        </Badge>
        <code
          style={{
            fontFamily: 'Consolas, monospace',
            fontSize:   13,
            color:      tokens.colorNeutralForeground2,
          }}
        >
          {entry.ref}
        </code>
      </div>
      <Body1 style={{ lineHeight: 1.55, color: tokens.colorNeutralForeground1 }}>
        {entry.text}
      </Body1>
      {entry.url && (
        <a
          href={entry.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display:        'inline-flex',
            alignItems:     'center',
            gap:            4,
            marginTop:      8,
            fontSize:       13,
            color:          accent,
            textDecoration: 'none',
            fontWeight:     600,
          }}
        >
          <Link20Regular style={{ width: 14, height: 14 }} />
          Source officielle
        </a>
      )}
    </div>
  );
}

function CyFunBlock({
  entries,
  accent,
}: {
  entries?: CyFunDetailEntry[];
  accent:   string;
}) {
  if (!entries || entries.length === 0) return null;
  return (
    <div
      style={{
        padding:      14,
        background:   tokens.colorNeutralBackground2,
        borderLeft:   `4px solid ${accent}`,
        borderRadius: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <Badge appearance="filled" style={{ background: accent, color: '#fff' }}>
          CCB CyFun
        </Badge>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          ({entries.length} ref{entries.length > 1 ? 's' : ''})
        </Caption1>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {entries.map((e, i) => (
          <div
            key={i}
            style={{
              paddingLeft:    12,
              borderLeft:     `2px solid ${tokens.colorNeutralStroke2}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <code style={{ fontFamily: 'Consolas, monospace', fontSize: 13, color: tokens.colorNeutralForeground2 }}>
                {e.ref}
              </code>
              <Badge
                appearance="filled"
                color={e.level === 'Essential' ? 'danger' : e.level === 'Important' ? 'warning' : 'success'}
              >
                {e.level}
              </Badge>
            </div>
            <Body1 style={{ lineHeight: 1.5, color: tokens.colorNeutralForeground1 }}>
              {e.text}
            </Body1>
            {e.url && (
              <a
                href={e.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 12, color: accent, textDecoration: 'none', fontWeight: 600 }}
              >
                <Link20Regular style={{ width: 12, height: 12 }} />
                CCB Cyberfundamentals Framework
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RemediationBlock({ remediation }: { remediation: ControlDetail['remediation'] }) {
  const { t } = useTranslation();
  return (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Summary */}
      <div
        style={{
          padding:      14,
          background:   tokens.colorBrandBackground2,
          borderRadius: 6,
          borderLeft:   `4px solid ${tokens.colorBrandForeground1}`,
        }}
      >
        <Body1 style={{ fontWeight: 600, color: tokens.colorBrandForeground1 }}>
          {remediation.summary}
        </Body1>
      </div>

      {/* Prerequisites */}
      {remediation.prerequisites && remediation.prerequisites.length > 0 && (
        <div>
          <Caption1 style={{ display: 'block', fontWeight: 700, marginBottom: 8, color: tokens.colorNeutralForeground2 }}>
            {t('controls.detail.rich.prerequisites', { defaultValue: 'Pré-requis' })}
          </Caption1>
          <ul style={{ margin: 0, paddingLeft: 20, color: tokens.colorNeutralForeground1, lineHeight: 1.7 }}>
            {remediation.prerequisites.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Steps */}
      <div>
        <Caption1 style={{ display: 'block', fontWeight: 700, marginBottom: 8, color: tokens.colorNeutralForeground2 }}>
          {t('controls.detail.rich.steps', { defaultValue: 'Étapes' })}
        </Caption1>
        <ol style={{ margin: 0, paddingLeft: 20, color: tokens.colorNeutralForeground1, lineHeight: 1.7 }}>
          {remediation.steps.map((s, i) => (
            <li key={i} style={{ marginBottom: 6 }}>{s.replace(/^\d+\.\s*/, '')}</li>
          ))}
        </ol>
      </div>

      {/* Verify */}
      {remediation.verify && (
        <div
          style={{
            padding:      12,
            background:   tokens.colorPaletteGreenBackground1,
            borderRadius: 6,
            borderLeft:   `4px solid ${tokens.colorPaletteGreenBorder2}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Checkmark20Filled style={{ color: tokens.colorPaletteGreenForeground2 }} />
            <Caption1 style={{ fontWeight: 700, color: tokens.colorPaletteGreenForeground2 }}>
              {t('controls.detail.rich.verify', { defaultValue: 'Vérification' })}
            </Caption1>
          </div>
          <Body1>{remediation.verify}</Body1>
        </div>
      )}

      {/* Links */}
      {remediation.links && remediation.links.length > 0 && (
        <div>
          <Caption1 style={{ display: 'block', fontWeight: 700, marginBottom: 6, color: tokens.colorNeutralForeground2 }}>
            {t('controls.detail.rich.docs', { defaultValue: 'Documentation' })}
          </Caption1>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {remediation.links.map((l, i) => (
              <a
                key={i}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display:        'inline-flex',
                  alignItems:     'center',
                  gap:            6,
                  fontSize:       13,
                  color:          tokens.colorBrandForeground1,
                  textDecoration: 'none',
                  fontWeight:     500,
                }}
              >
                <Link20Regular style={{ width: 14, height: 14 }} />
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* unused helper kept for future variant : numbered circle icon for steps */
export { NumberCircle020Regular };
